import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const { shop, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(null);

  const features = [
    "Unlimited client intake sessions",
    "Unlimited AI-generated summaries",
    "Custom shop profile",
    "Instantly auto-generate detailed service sheets",
    "Track + manage every job in one dashboard",
    "Your entire business — centralized & accessible",
    "Captures and structures client requests for you",
    "Smart AI intake replaces back-and-forth messaging",
    "Google Calendar integration",
    "Priority support"
  ];

  const handleSubscribe = async (plan) => {
    setLoading(plan);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center p-6">
      <div className="max-w-5xl w-full">
        <div className="text-center mb-12">
          {
            shop?.subscription_status === "trial" && shop?.trial_days_remaining <= 0 && (
              <>
                <div className="inline-flex items-center gap-2 bg-[#d4af37]/20 border border-[#d4af37]/30 px-4 py-2 rounded-full mb-6">
                  <AlertCircle className="w-5 h-5 text-[#d4af37]" />
                  <span className="text-[#d4af37] font-medium">Trial Ended</span>
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-[#f8f8f8] mb-4">
                  Your 7-Day Trial Has Ended
                </h1>
              </>
            )
          }
          <p className="text-lg text-[#f8f8f8]/70 max-w-2xl mx-auto">
            Continue transforming your business experience with a subscription
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Monthly Plan */}
          <Card className="bg-[#0a1a1f]/80 backdrop-blur-sm border-[#d4af37]/30 p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-[#f8f8f8] mb-2">Monthly Plan</h3>
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-5xl font-bold text-[#00ced1]">$24.99</span>
                <span className="text-[#f8f8f8]/60">/month</span>
              </div>
              <p className="text-sm text-[#f8f8f8]/60">Cancel anytime</p>
            </div>

            <div className="space-y-3 mb-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#00ced1] flex-shrink-0 mt-0.5" />
                  <span className="text-[#f8f8f8]/80 text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="subscribe-monthly-btn"
              onClick={() => handleSubscribe("monthly")}
              disabled={loading === "monthly"}
              className="w-full bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              {loading === "monthly" ? "Processing..." : "Subscribe Monthly"}
            </Button>
          </Card>

          {/* Annual Plan */}
          <Card className="bg-gradient-to-br from-[#005f6b] to-[#00ced1] border-2 border-[#d4af37] p-8 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-[#d4af37] text-[#0a1a1f] px-4 py-1 rounded-full text-sm font-semibold">
                Save ~$50
              </span>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-[#f8f8f8] mb-2">Annual Plan</h3>
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-5xl font-bold text-[#f8f8f8]">$249</span>
                <span className="text-[#f8f8f8]/80">/year</span>
              </div>
              <p className="text-sm text-[#f8f8f8]/80">Best value • 2 months free</p>
            </div>

            <div className="space-y-3 mb-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#f8f8f8] flex-shrink-0 mt-0.5" />
                  <span className="text-[#f8f8f8] text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="subscribe-annual-btn"
              onClick={() => handleSubscribe("annual")}
              disabled={loading === "annual"}
              className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              {loading === "annual" ? "Processing..." : "Subscribe Annually"}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;
